// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataGrid, GridActionsCellItem, GridRowModes, GridRowEditStopReasons, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { AlertColor } from '@mui/material';
import { apiUrl } from '../utils/api';
import { formatCurrency, formatNumber } from '../utils/numberFormatter';
import { dataGridSx } from '../utils/dataGridStyles';
import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Autocomplete,
  Badge,
  Checkbox,
  Chip,
  LinearProgress,
  Menu
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContractDocuments from '../components/ContractDocuments';

const emptyForm = {
  contract_number: '',
  title: '',
  original_value: '',
  currency: 'SGD',
  signed_date: '',
  rate_sheet_id: '',
  contract_type: 'contract'
};

// Budget Summary Component
const BudgetSummary = ({ contractId }: { contractId: number }) => {
  const [budgetInfo, setBudgetInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchBudget = async () => {
      try {
        setLoading(true);
        const response = await fetch(apiUrl(`/api/contracts/${contractId}/budget-summary`));
        if (!response.ok) return;
        const data = await response.json();
        setBudgetInfo(data);
      } catch (error) {
        console.error('Error fetching budget:', error);
      } finally {
        setLoading(false);
      }
    };

    if (contractId) {
      fetchBudget();
    }
  }, [contractId]);

  if (loading) return <LinearProgress />;
  if (!budgetInfo) return null;

  const percentageUsed = budgetInfo.percentage_used || 0;
  const isOverBudget = budgetInfo.status === 'over_budget';

  return (
    <Box sx={{ mt: 2, p: 2, bgcolor: isOverBudget ? '#ffebee' : '#e8f5e9', borderRadius: 1 }}>
      <Typography variant="h6" gutterBottom>Budget Summary</Typography>
      {/* @ts-ignore */}
      <Grid container spacing={2}>
        {/* @ts-ignore */}
        <Grid item xs={6} md={3}>
          <Typography variant="caption" color="text.secondary">Contract Value</Typography>
          <Typography variant="body1" fontWeight="bold">
            ${budgetInfo.budget_summary.contract_value.toLocaleString()}
          </Typography>
        </Grid>
        {/* @ts-ignore */}
        <Grid item xs={6} md={3}>
          <Typography variant="caption" color="text.secondary">PO Committed</Typography>
          <Typography variant="body1" fontWeight="bold">
            ${budgetInfo.budget_summary.po_committed.toLocaleString()}
          </Typography>
        </Grid>
        {/* @ts-ignore */}
        <Grid item xs={6} md={3}>
          <Typography variant="caption" color="text.secondary">MR Committed</Typography>
          <Typography variant="body1" fontWeight="bold">
            ${budgetInfo.budget_summary.mr_committed.toLocaleString()}
          </Typography>
        </Grid>
        {/* @ts-ignore */}
        <Grid item xs={6} md={3}>
          <Typography variant="caption" color="text.secondary">Remaining</Typography>
          <Typography variant="body1" fontWeight="bold" color={isOverBudget ? 'error' : 'success'}>
            ${budgetInfo.budget_summary.remaining.toLocaleString()}
          </Typography>
        </Grid>
      </Grid>
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption">Budget Usage: {percentageUsed.toFixed(1)}%</Typography>
        <LinearProgress
          variant="determinate"
          value={Math.min(percentageUsed, 100)}
          color={percentageUsed > 90 ? 'error' : percentageUsed > 75 ? 'warning' : 'success'}
          sx={{ mt: 0.5 }}
        />
      </Box>
      {isOverBudget && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Budget exceeded!
        </Alert>
      )}
    </Box>
  );
};

const Contracts = ({ projectId, access = 'none', userId = null, deleteAllAccess = 'none' }) => {
  const canWrite = access === 'write';
  const canDeleteAll = deleteAllAccess === 'write';
  const [loading, setLoading] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rateSheets, setRateSheets] = useState([]);
  const [rateSheetItems, setRateSheetItems] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractRowModesModel] = useState({});
  const [budgetLineRowModesModel, setBudgetLineRowModesModel] = useState({});
  const [pendingSaveId, setPendingSaveId] = useState(null);

  // Removed: expandedRows and contractVOs (VOs now shown at same level)

  // Legacy states for dialog (will be simplified)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [formValues, setFormValues] = useState(emptyForm);

  const [saving, setSaving] = useState(false);
  const [pageError, setPageError] = useState('');
  const [dialogError, setDialogError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedContractForDocs, setSelectedContractForDocs] = useState<any>(null);

  // Import Design Changes states
  const [importDCDialogOpen, setImportDCDialogOpen] = useState(false);
  const [designChanges, setDesignChanges] = useState([]);
  const [selectedDCs, setSelectedDCs] = useState<string[]>([]);
  const [importingDC, setImportingDC] = useState(false);
  const [importDCError, setImportDCError] = useState('');

  // Variation Order dialog states (NEW HIERARCHICAL SYSTEM)
  const [voDialogOpen, setVoDialogOpen] = useState(false);
  const [voFormValues, setVoFormValues] = useState(emptyForm);
  const [voDialogError, setVoDialogError] = useState('');
  const [creatingVO, setCreatingVO] = useState(false);
  const [voSelectedDCs, setVoSelectedDCs] = useState<string[]>([]);
  const [voSelectedBoqItems, setVoSelectedBoqItems] = useState<{[dcNumber: string]: number[]}>({});
  const [voExpandedDCs, setVoExpandedDCs] = useState<string[]>([]);
  const [voDcBoqData, setVoDcBoqData] = useState<{[dcNumber: string]: any[]}>({});
  const [voLoadingBoqData, setVoLoadingBoqData] = useState(false);
  const [budgetLineLoading, setBudgetLineLoading] = useState(false);

  // Excel Import/Export States
  const [excelImporting, setExcelImporting] = useState(false);
  const [excelExporting, setExcelExporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importResults, setImportResults] = useState<any>(null);
  const [currentImportFile, setCurrentImportFile] = useState<File | null>(null);

  // Delete Dialog States
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState<'contract' | 'vo' | 'budgetLine'>('contract');
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);

  // Delete All Dialog States
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  // Add Contract/VO Menu State
  const [addMenuAnchorEl, setAddMenuAnchorEl] = useState<null | HTMLElement>(null);
  const addMenuOpen = Boolean(addMenuAnchorEl);

  const saveButtonRef = useRef(null);
  const processingRef = useRef(false);



  const fetchContracts = useCallback(() => {
    if (!projectId || access === 'none') return;
    setLoading(true);
    setPageError('');
    fetch(apiUrl(`/api/contracts?project_id=${projectId}&include_lines=true`), { headers: { ...(userId ? { 'X-User-ID': userId } : {}) } })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load contracts');
        return data;
      })
      .then(data => {
        // Show all contracts and VOs at the same level (no filtering)
        const allContracts = Array.isArray(data) ? data : [];
        setContracts(allContracts);
        setSelectedContract(prev => {
          if (!prev) return prev;
          return allContracts.find(item => item.contract_id === prev.contract_id) || null;
        });
      })
      .catch(err => setPageError(err.message))
      .finally(() => setLoading(false));
  }, [projectId, access, userId]);

  const fetchCategories = useCallback(() => {
    if (!projectId || access === 'none') return;
    fetch(apiUrl(`/api/budget-categories?project_id=${projectId}`))
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load categories');
        return data;
      })
      .then(data => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setCategories([]));
  }, [projectId, access]);

  // Removed: fetchVOsForContract (VOs now fetched with contracts at same level)

  // Excel Export Handler
  const handleExportExcel = async () => {
    try {
      setExcelExporting(true);
      const url = projectId
        ? apiUrl(`/api/contracts/export?project_id=${projectId}`)
        : apiUrl('/api/contracts/export');

      const response = await fetch(url, {
        credentials: 'include',
        headers: { ...(userId ? { 'X-User-ID': userId } : {}) }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to export contracts');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `contracts_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      setSnackbar({ open: true, message: 'Contracts exported successfully!', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setExcelExporting(false);
    }
  };

  // Excel Template Download Handler
  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(apiUrl('/api/contracts/template'), {
        credentials: 'include',
        headers: { ...(userId ? { 'X-User-ID': userId } : {}) }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to download template');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'contracts_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      setSnackbar({ open: true, message: 'Template downloaded successfully!', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  // Excel Import Handler
  const handleImportExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset file input immediately
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    try {
      setExcelImporting(true);
      const formData = new FormData();
      formData.append('file', file);

      const url = projectId
        ? apiUrl(`/api/contracts/import?project_id=${projectId}`)
        : apiUrl('/api/contracts/import');

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { ...(userId ? { 'X-User-ID': userId } : {}) },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to import contracts');
      }

      // Store results and show dialog
      setImportResults(data);
      setCurrentImportFile(file);
      setImportDialogOpen(true);

      // Refresh contracts list
      fetchContracts();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setExcelImporting(false);
    }
  };

  const handleImportDialogClose = () => {
    setImportDialogOpen(false);
    setImportResults(null);
    setCurrentImportFile(null);
  };

  const fetchRateSheets = useCallback(() => {
    if (!projectId || access === 'none') return;
    console.log('🔄 Fetching rate sheets for project:', projectId);
    fetch(apiUrl(`/api/rate-sheets?project_id=${projectId}`))
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load rate sheets');
        return data;
      })
      .then(data => {
        console.log('📥 Received rate sheets:', data);
        setRateSheets(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error('❌ Failed to fetch rate sheets:', err);
        setRateSheets([]);
      });

    // Fetch Budget Line Items
    if (projectId) {
      fetch(apiUrl(`/api/budget-line-items?project_id=${projectId}`))
        .then(res => {
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}: ${res.statusText}`);
          }
          return res.json();
        })
        .then(data => {
          setRateSheetItems(Array.isArray(data) ? data : []);
        })
        .catch(err => {
          console.error('Failed to fetch budget line items:', err);
          setRateSheetItems([]); // Set empty array on error
        });
    }
  }, [projectId, access]);

  const fetchRateSheetItems = useCallback((rateSheetId) => {
    if (rateSheetId) {
      console.log('🔄 Fetching rate sheet items for rate sheet:', rateSheetId);
      fetch(apiUrl(`/api/rate-sheets/${rateSheetId}/items`))
        .then(async res => {
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || 'Failed to load rate sheet items');
          return data;
        })
        .then(data => {
          console.log('📋 Received rate sheet items:', data);
          setRateSheetItems(Array.isArray(data) ? data : []);
        })
        .catch(err => {
          console.error('❌ Failed to fetch rate sheet items:', err);
          setRateSheetItems([]);
        });
    } else {
      setRateSheetItems([]);
    }
  }, []);

  const fetchDesignChanges = useCallback(() => {
    if (!projectId || access === 'none') return;
    fetch(apiUrl(`/api/design-changes?project_id=${projectId}`))
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load design changes');
        return data;
      })
      .then(data => setDesignChanges(Array.isArray(data) ? data : []))
      .catch(err => {
        console.error('Failed to fetch design changes:', err);
        setDesignChanges([]);
      });
  }, [projectId, access]);

  useEffect(() => {
    if (!projectId || access === 'none') {
      setContracts([]);
      setCategories([]);
      setRateSheets([]);
      setDesignChanges([]);
      setSelectedContract(null);
      setPageError('');
      return;
    }
    fetchCategories();
    fetchRateSheets();
    fetchDesignChanges();
    fetchContracts();
  }, [projectId, access, fetchCategories, fetchRateSheets, fetchDesignChanges, fetchContracts]);

  // Handle pending save operations after state updates
  useEffect(() => {
    if (!pendingSaveId || !selectedContract || processingRef.current) {
      return;
    }

    const performSave = async () => {
      try {
        // Prevent duplicate processing
        processingRef.current = true;
        setBudgetLineLoading(true); // Show loading in budget lines DataGrid
        setLoading(true); // Show loading in main contracts table

        console.log('💾 Executing pending save for ID:', pendingSaveId);
        console.log('🔍 selectedContract in useEffect:', selectedContract);
        console.log('🔍 selectedContract.lines:', selectedContract.lines);

        // Find the line to save with current state values
        const lineToSave = selectedContract.lines?.find(line => line.id === pendingSaveId);
        if (!lineToSave) {
          console.error('Line to save not found:', pendingSaveId);
          setSnackbar({ open: true, message: 'Error: Budget line not found', severity: 'error' });
          setPendingSaveId(null);
          setBudgetLineLoading(false);
          setLoading(false);
          return;
        }

        console.log('🔍 Line to save:', lineToSave);

        // For now, update the contract with the new/updated lines
        // Since the backend doesn't have individual budget line endpoints,
        // we'll update the entire contract with the modified lines
        const updatedLines = selectedContract.lines || [];

        // If it's a new line, replace temp ID with a proper structure
        const linesToSave = updatedLines.map(line => ({
          id: String(line.id).startsWith('temp_') ? undefined : line.id,
          line_no: line.line_no,
          description: line.description,
          qty: line.qty,
          uom: line.uom,
          unit_price: line.unit_price,
          category_id: line.category_id || undefined
        }));

        const response = await fetch(apiUrl(`/api/contracts/${selectedContract.contract_id}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...(userId ? { 'X-User-ID': userId } : {}) },
          body: JSON.stringify({
            contract_number: selectedContract.contract_number,
            title: selectedContract.title,
            original_value: selectedContract.original_value,
            currency: selectedContract.currency,
            signed_date: selectedContract.signed_date,
            rate_sheet_id: selectedContract.rate_sheet_id,
            lines: linesToSave
          })
        });

        if (response.ok) {
          const updatedContract = await response.json();
          setSelectedContract(updatedContract);

          // Refetch all contracts to update total values immediately
          await fetchContracts();

          setSnackbar({
            open: true,
            message: String(pendingSaveId).startsWith('temp_') ? 'Budget line added successfully' : 'Budget line updated successfully',
            severity: 'success'
          });
        } else {
          const error = await response.json();
          setSnackbar({ open: true, message: error.error || 'Failed to save budget line', severity: 'error' });
        }
      } catch (saveError) {
        console.error('Error saving budget line:', saveError);
        setSnackbar({ open: true, message: 'Failed to save budget line', severity: 'error' });
      } finally {
        setPendingSaveId(null);
        processingRef.current = false;
        setBudgetLineLoading(false); // Hide loading
        setLoading(false); // Hide loading in main contracts table
      }
    };

    performSave();
  }, [pendingSaveId, selectedContract, userId, fetchContracts]);

  // Process row update for budget lines (inline DataGrid editing)
  const processBudgetLineRowUpdate = useCallback(async (newRow) => {
    console.log('🔄 processBudgetLineRowUpdate called with:', newRow);
    
    if (!canWrite) {
      throw new Error('You do not have permission to edit budget lines.');
    }
    
    if (!selectedContract) {
      throw new Error('No contract selected.');
    }

    // For temp IDs (new lines), only update local state but don't make API calls
    // The manual save button will handle the API call
    if (String(newRow.id).startsWith('temp_')) {
      console.log('🔄 Updating local state only for new line (temp ID):', newRow.id);
      
      // Update the local state with the edited values
      const updatedLines = selectedContract.lines?.map(line => 
        line.id === newRow.id ? { ...line, ...newRow } : line
      ) || [];

      console.log('📝 Updated temp line in state:', newRow);
      
      // Update selectedContract state
      const updatedContract = { ...selectedContract, lines: updatedLines };
      setSelectedContract(updatedContract);
      
      return newRow; // Return the updated row without making API call
    }

    // For existing lines (non-temp IDs), update state and make API call
    try {
      setBudgetLineLoading(true); // Show loading state
      setLoading(true); // Show loading in main contracts table

      // Update the local state immediately
      const updatedLines = selectedContract.lines?.map(line =>
        line.id === newRow.id ? { ...line, ...newRow } : line
      ) || [];

      console.log('🔄 Updated budget line:', newRow);
      console.log('📋 All lines after update:', updatedLines);

      // Update selectedContract state
      const updatedContract = { ...selectedContract, lines: updatedLines };
      setSelectedContract(updatedContract);

      // Prepare the payload for the API call
      const linesToSave = updatedLines.map(line => ({
        id: String(line.id).startsWith('temp_') ? undefined : line.id,
        line_no: line.line_no,
        description: line.description,
        qty: line.qty,
        uom: line.uom,
        unit_price: line.unit_price,
        category_id: line.category_id || undefined
      }));

      console.log('🚀 Saving budget lines via API:', linesToSave);

      const response = await fetch(apiUrl(`/api/contracts/${selectedContract.contract_id}`), {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          ...(userId ? { 'X-User-ID': userId } : {})
        },
        body: JSON.stringify({
          contract_number: selectedContract.contract_number,
          title: selectedContract.title,
          original_value: selectedContract.original_value,
          currency: selectedContract.currency,
          signed_date: selectedContract.signed_date,
          rate_sheet_id: selectedContract.rate_sheet_id,
          lines: linesToSave
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update budget line');
      }

      const updatedContractFromAPI = await response.json();
      console.log('✅ Budget line updated successfully:', updatedContractFromAPI);

      // Update local state with response from API
      setSelectedContract(updatedContractFromAPI);

      // Refetch all contracts to update total values immediately
      await fetchContracts();

      setBudgetLineLoading(false); // Hide loading state
      setLoading(false); // Hide loading in main contracts table

      // Show success message
      setSnackbar({
        open: true,
        message: 'Budget line updated successfully',
        severity: 'success'
      });

      return newRow; // Return the updated row

    } catch (error) {
      setBudgetLineLoading(false); // Hide loading state on error
      setLoading(false); // Hide loading in main contracts table on error
      console.error('❌ Error updating budget line:', error);
      setSnackbar({
        open: true,
        message: `Error updating budget line: ${error.message}`,
        severity: 'error'
      });
      throw error; // This will revert the row to its original state
    }
  }, [canWrite, selectedContract, userId, fetchContracts]);

  // selectedContract is now managed as state instead of computed

  // Menu handlers
  const handleOpenAddMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAddMenuAnchorEl(event.currentTarget);
  };

  const handleCloseAddMenu = () => {
    setAddMenuAnchorEl(null);
  };

  const handleOpenCreate = () => {
    handleCloseAddMenu();
    setDialogMode('create');
    setDialogError('');
    setFormValues({ ...emptyForm });
    setDialogOpen(true);
  };

  const handleOpenCreateVOFromMenu = () => {
    handleCloseAddMenu();
    handleOpenCreateVO();
  };

  const handleOpenEdit = useCallback(async (contract) => {
    if (!contract) return;
    setDialogMode('edit');
    setDialogError('');
    setSelectedContract(contract);
    console.log('🔍 Opening edit for contract:', contract);
    console.log('🔍 Contract rate_sheet_id value:', contract.rate_sheet_id);
    const formData = {
      contract_number: contract.contract_number || '',
      title: contract.title || '',
      original_value: contract.original_value !== null && contract.original_value !== undefined ? String(contract.original_value) : '',
      currency: contract.currency || 'SGD',
      signed_date: contract.signed_date || '',
      rate_sheet_id: contract.rate_sheet_id || '',
      contract_type: contract.contract_type || 'contract'
    };
    console.log('🔍 Setting form values with rate_sheet_id:', formData.rate_sheet_id);
    setFormValues(formData);

    // Fetch rate sheet items if a rate sheet is selected
    if (formData.rate_sheet_id) {
      fetchRateSheetItems(formData.rate_sheet_id);
    }

    // If editing a VO, fetch design changes and pre-select existing BOQ items
    if (contract.contract_type === 'variation_order') {
      // Fetch design changes
      try {
        const dcResponse = await fetch(apiUrl(`/api/design-changes?project_id=${projectId}`), {
          headers: { ...(userId ? { 'X-User-ID': userId } : {}) }
        });
        const dcData = await dcResponse.json();
        setDesignChanges(Array.isArray(dcData) ? dcData : []);
      } catch (err) {
        console.error('Failed to fetch design changes:', err);
      }

      // Fetch existing BOQ items for this VO
      try {
        const boqResponse = await fetch(apiUrl(`/api/contracts/${contract.contract_id}/lines`), {
          headers: { ...(userId ? { 'X-User-ID': userId } : {}) }
        });
        const boqData = await boqResponse.json();
        console.log('📦 Fetched VO BOQ items:', boqData);

        // Group BOQ items by their source DC and pre-select them
        const selectedByDC: Record<string, number[]> = {};
        const dcBoqDataMap: Record<string, any[]> = {};
        const expandedDCs: string[] = [];

        if (Array.isArray(boqData)) {
          for (const item of boqData) {
            console.log('📝 Processing BOQ item:', item);
            const sourceChangeNumber = item.source_change_number;
            if (sourceChangeNumber) {
              if (!selectedByDC[sourceChangeNumber]) {
                selectedByDC[sourceChangeNumber] = [];
                expandedDCs.push(sourceChangeNumber); // Auto-expand DCs with selected items
              }
              if (item.source_dc_boq_line_id) {
                selectedByDC[sourceChangeNumber].push(item.source_dc_boq_line_id);
              }
            }
          }

          console.log('✅ Selected items by DC:', selectedByDC);
          console.log('✅ DCs to expand:', expandedDCs);

          // Fetch BOQ data for each DC that has items
          for (const dcNum of Object.keys(selectedByDC)) {
            try {
              const dcBoqResponse = await fetch(apiUrl(`/api/design-changes/${dcNum}/boq`));
              const dcBoqData = await dcBoqResponse.json();
              dcBoqDataMap[dcNum] = Array.isArray(dcBoqData) ? dcBoqData : [];
              console.log(`📋 Fetched BOQ for DC ${dcNum}:`, dcBoqDataMap[dcNum]);
            } catch (err) {
              console.error(`❌ Failed to fetch BOQ for DC ${dcNum}:`, err);
            }
          }
        }

        // Also fetch DCs linked via Contract Design Changes page (may not have budget lines)
        try {
          const cdcResponse = await fetch(apiUrl(`/api/contract-design-changes?contract_id=${contract.contract_id}`), {
            headers: { ...(userId ? { 'X-User-ID': userId } : {}) }
          });
          const cdcData = await cdcResponse.json();
          console.log('📋 Fetched Contract Design Changes:', cdcData);

          if (Array.isArray(cdcData)) {
            for (const cdc of cdcData) {
              const changeNumber = cdc.change_number;
              if (changeNumber && !selectedByDC[changeNumber]) {
                // DC is linked but has no budget lines yet
                // Fetch BOQ data for this DC first
                try {
                  const dcBoqResponse = await fetch(apiUrl(`/api/design-changes/${changeNumber}/boq`));
                  const dcBoqData = await dcBoqResponse.json();
                  const boqItems = Array.isArray(dcBoqData) ? dcBoqData : [];
                  dcBoqDataMap[changeNumber] = boqItems;

                  // Select ALL BOQ items from this DC by default (since it's linked)
                  const allBoqLineIds = boqItems.map((item: any) => item.dc_boq_line_id).filter((id: any) => id);
                  selectedByDC[changeNumber] = allBoqLineIds;
                  expandedDCs.push(changeNumber);

                  console.log(`📋 Auto-selected ${allBoqLineIds.length} BOQ items for linked DC ${changeNumber}`);
                } catch (err) {
                  console.error(`❌ Failed to fetch BOQ for DC ${changeNumber}:`, err);
                  // Even if fetch fails, add DC to list with empty selection
                  selectedByDC[changeNumber] = [];
                  expandedDCs.push(changeNumber);
                }
              }
            }
          }
        } catch (err) {
          console.error('❌ Failed to fetch Contract Design Changes:', err);
        }

        setVoSelectedBoqItems(selectedByDC);
        setVoDcBoqData(dcBoqDataMap);
        setVoExpandedDCs(expandedDCs); // Auto-expand DCs with pre-selected items
        console.log('✅ Pre-selection complete');
      } catch (err) {
        console.error('❌ Failed to fetch VO BOQ items:', err);
      }
    }

    setDialogOpen(true);
  }, [fetchRateSheetItems, projectId, userId]);

  const handleCloseDialog = () => {
    // Clear focus to prevent accessibility warnings about focused elements being hidden
    if (document.activeElement && 'blur' in document.activeElement) {
      (document.activeElement as HTMLElement).blur();
    }
    setDialogOpen(false);
    setSaving(false);
    setDialogError('');
    setRateSheetItems([]); // Clear rate sheet items when dialog closes

    // Clear VO-related state when closing dialog
    setVoSelectedBoqItems({});
    setVoDcBoqData({});
    setVoExpandedDCs([]);
  };

  // Import Design Changes handlers
  const handleOpenImportDC = () => {
    if (!selectedContract) {
      setSnackbar({ open: true, message: 'Please select a contract first', severity: 'warning' });
      return;
    }
    setImportDCError('');
    setSelectedDCs([]);
    setImportDCDialogOpen(true);
  };

  const handleCloseImportDC = () => {
    setImportDCDialogOpen(false);
    setImportDCError('');
    setSelectedDCs([]);
    setImportingDC(false);
  };

  const handleDCSelect = (changeNumber: string) => {
    setSelectedDCs(prev =>
      prev.includes(changeNumber)
        ? prev.filter(cn => cn !== changeNumber)
        : [...prev, changeNumber]
    );
  };

  const handleImportDCSubmit = async () => {
    if (selectedDCs.length === 0) {
      setImportDCError('Please select at least one design change');
      return;
    }
    if (!selectedContract) return;

    setImportingDC(true);
    setImportDCError('');

    try {
      const response = await fetch(apiUrl(`/api/contracts/${selectedContract.contract_id}/compile-dc-boq`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(userId ? { 'X-User-ID': userId } : {})
        },
        body: JSON.stringify({
          change_numbers: selectedDCs,
          compiled_by: userId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to import design changes');
      }

      setSnackbar({
        open: true,
        message: `Successfully imported: ${data.merged_count} merged, ${data.added_count} added`,
        severity: 'success'
      });

      handleCloseImportDC();
      fetchContracts(); // Refresh to get updated contract lines
    } catch (err: any) {
      setImportDCError(err.message);
    } finally {
      setImportingDC(false);
    }
  };

  // Variation Order handlers (UPDATED - NO PARENT CONTRACT)
  const handleOpenCreateVO = useCallback(() => {
    setVoDialogError('');
    setVoFormValues({
      ...emptyForm,
      currency: 'SGD'
    });
    setVoSelectedDCs([]);
    setVoSelectedBoqItems({});
    setVoExpandedDCs([]);
    setVoDcBoqData({});

    // Fetch design changes for DC selection
    fetch(apiUrl(`/api/design-changes?project_id=${projectId}`), {
      headers: { ...(userId ? { 'X-User-ID': userId } : {}) }
    })
      .then(res => res.json())
      .then(data => {
        setDesignChanges(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error('Failed to fetch design changes:', err);
      });

    setVoDialogOpen(true);
  }, [projectId, userId]);

  const handleCloseVODialog = () => {
    setVoDialogOpen(false);
    setCreatingVO(false);
    setVoDialogError('');
    setVoSelectedDCs([]);
    setVoSelectedBoqItems({});
    setVoExpandedDCs([]);
    setVoDcBoqData({});
  };

  const handleVOFormChange = (evt) => {
    const { name, value } = evt.target;
    if (name === 'rate_sheet_id') {
      fetchRateSheetItems(value);
    }
    setVoFormValues(prev => ({ ...prev, [name]: value }));
  };

  // Fetch BOQ items for a specific DC
  const fetchVODCBoqItems = async (changeNumber: string) => {
    if (voDcBoqData[changeNumber]) return; // Already fetched

    setVoLoadingBoqData(true);
    try {
      const response = await fetch(apiUrl(`/api/design-changes/${changeNumber}/boq`));
      if (!response.ok) throw new Error('Failed to fetch BOQ items');
      const data = await response.json();
      setVoDcBoqData(prev => ({ ...prev, [changeNumber]: Array.isArray(data) ? data : [] }));
    } catch (err) {
      console.error('Failed to fetch DC BOQ items:', err);
      setVoDcBoqData(prev => ({ ...prev, [changeNumber]: [] }));
    } finally {
      setVoLoadingBoqData(false);
    }
  };

  // Toggle DC expand/collapse
  const handleVOToggleDCExpand = (changeNumber: string) => {
    const isExpanded = voExpandedDCs.includes(changeNumber);
    if (isExpanded) {
      setVoExpandedDCs(prev => prev.filter(cn => cn !== changeNumber));
    } else {
      setVoExpandedDCs(prev => [...prev, changeNumber]);
      fetchVODCBoqItems(changeNumber); // Fetch BOQ items when expanding
    }
  };

  // Handle DC checkbox (parent) - selects/deselects all BOQ items
  const handleVODCSelect = (changeNumber: string) => {
    const boqItems = voDcBoqData[changeNumber] || [];
    const selectedItems = voSelectedBoqItems[changeNumber] || [];

    if (selectedItems.length === boqItems.length && boqItems.length > 0) {
      // All selected, deselect all
      setVoSelectedBoqItems(prev => ({ ...prev, [changeNumber]: [] }));
    } else {
      // Not all selected, select all
      const allBoqIds = boqItems.map(item => item.dc_boq_line_id);
      setVoSelectedBoqItems(prev => ({ ...prev, [changeNumber]: allBoqIds }));
    }
  };

  // Handle individual BOQ item checkbox (child)
  const handleVOBoqItemSelect = (changeNumber: string, boqLineId: number) => {
    setVoSelectedBoqItems(prev => {
      const currentItems = prev[changeNumber] || [];
      const isSelected = currentItems.includes(boqLineId);

      if (isSelected) {
        return { ...prev, [changeNumber]: currentItems.filter(id => id !== boqLineId) };
      } else {
        return { ...prev, [changeNumber]: [...currentItems, boqLineId] };
      }
    });
  };

  const handleCreateVOSubmit = async () => {
    if (!canWrite) {
      setVoDialogError('You do not have permission to create variation orders.');
      return;
    }
    const trimmedNumber = voFormValues.contract_number.trim();
    if (!trimmedNumber) {
      setVoDialogError('VO number is required.');
      return;
    }

    // Build array of selected BOQ items
    const selectedBoqItemsList: any[] = [];
    Object.keys(voSelectedBoqItems).forEach(changeNumber => {
      const boqLineIds = voSelectedBoqItems[changeNumber] || [];
      boqLineIds.forEach(dc_boq_line_id => {
        selectedBoqItemsList.push({ change_number: changeNumber, dc_boq_line_id });
      });
    });

    // Allow creating VOs without BOQ items (blank VOs)
    // Users can add budget lines manually later using "Add Budget Line" button

    setCreatingVO(true);
    setVoDialogError('');

    const payload = {
      project_id: projectId,
      contract_number: trimmedNumber,
      title: voFormValues.title.trim() || null,
      original_value: '0',  // VOs don't have original value, only BOQ value
      currency: voFormValues.currency || 'SGD',
      signed_date: voFormValues.signed_date || null,
      rate_sheet_id: voFormValues.rate_sheet_id || null,
      contract_type: 'variation_order',
      boq_items: selectedBoqItemsList,
      created_by: userId ? String(userId) : null
    };

    try {
      const response = await fetch(apiUrl(`/api/contracts`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(userId ? { 'X-User-ID': userId } : {})
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create variation order');
      }

      setSnackbar({
        open: true,
        message: `Variation order created successfully with ${data.total_imported || selectedBoqItemsList.length} BOQ item(s)`,
        severity: 'success'
      });

      handleCloseVODialog();
      fetchContracts(); // Refresh to show new VO
      window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
    } catch (err: any) {
      setVoDialogError(err.message);
    } finally {
      setCreatingVO(false);
    }
  };

  const handleFormChange = evt => {
    const { name, value } = evt.target;
    if (name === 'rate_sheet_id') {
      console.log('🔄 Rate sheet selection changed to:', value);
      fetchRateSheetItems(value);
    }
    setFormValues(prev => ({ ...prev, [name]: value }));
  };



  const preparePayload = () => {
    console.log('🔍 Preparing payload with formValues:', formValues);
    console.log('🔍 FormValues rate_sheet_id value:', formValues.rate_sheet_id);
    const payload: any = {
      project_id: projectId,
      contract_number: formValues.contract_number.trim(),
      title: formValues.title.trim() || null,
      original_value: formValues.original_value,
      currency: formValues.currency || 'SGD',
      signed_date: formValues.signed_date || null,
      rate_sheet_id: formValues.rate_sheet_id || null
    };

    // When editing, preserve parent_contract_id and contract_type (important for VOs)
    if (dialogMode === 'edit' && selectedContract) {
      if (selectedContract.parent_contract_id) {
        payload.parent_contract_id = selectedContract.parent_contract_id;
      }
      if (selectedContract.contract_type) {
        payload.contract_type = selectedContract.contract_type;
      }
    }

    console.log('📤 Final payload to send:', payload);
    console.log('📤 Payload rate_sheet_id value:', payload.rate_sheet_id);
    return payload;
  };

  const handleSubmit = () => {
    if (!canWrite) {
      setDialogError('You do not have permission to save contracts.');
      return;
    }
    if (!projectId) {
      setDialogError('Select a project before saving.');
      return;
    }
    const trimmedNumber = formValues.contract_number.trim();
    if (!trimmedNumber) {
      setDialogError('Contract number is required.');
      return;
    }
    if (formValues.original_value === '') {
      setDialogError('Original value is required.');
      return;
    }
    const payload = preparePayload();
    setSaving(true);
    const url = dialogMode === 'create'
      ? apiUrl('/api/contracts')
      : apiUrl(`/api/contracts/${selectedContract.contract_id}`);
    const method = dialogMode === 'create' ? 'POST' : 'PUT';
    console.log(`🚀 Making ${method} request to ${url} with payload:`, payload);
    fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', ...(userId ? { 'X-User-ID': userId } : {}) },
      body: JSON.stringify(payload)
    })
      .then(async res => {
        const data = await res.json();
        console.log('📥 Server response:', { status: res.status, data });
        if (!res.ok) throw new Error(data.error || 'Save failed');
        return data;
      })
      .then(async (data) => {
        console.log('✅ Contract saved with rate_sheet_id:', data.rate_sheet_id, 'rate_sheet_name:', data.rate_sheet_name);

        // If editing a VO, update BOQ items based on selection
        if (dialogMode === 'edit' && formValues.contract_type === 'variation_order' && selectedContract) {
          try {
            // Collect all selected BOQ items
            const selectedBoqItemsList: any[] = [];
            Object.keys(voSelectedBoqItems).forEach(dcNum => {
              const dcBoqItems = voDcBoqData[dcNum] || [];
              const selectedIds = voSelectedBoqItems[dcNum] || [];
              selectedIds.forEach(boqLineId => {
                const boqItem = dcBoqItems.find((item: any) => item.dc_boq_line_id === boqLineId);
                if (boqItem) {
                  selectedBoqItemsList.push({
                    source_dc: dcNum,
                    source_dc_boq_line_id: boqLineId,
                    bli_id: boqItem.bli_id,
                    qty: boqItem.qty,
                    unit_price: boqItem.unit_price,
                    notes: boqItem.notes || ''
                  });
                }
              });
            });

            // Collect ONLY DCs that have at least one item selected
            // This ensures CDC relationships sync with actual VO content
            const allSelectedDCs = Object.keys(voSelectedBoqItems).filter(
              dcNum => voSelectedBoqItems[dcNum] && voSelectedBoqItems[dcNum].length > 0
            );

            // Update VO BOQ items
            setBudgetLineLoading(true); // Show loading in Budget Lines section
            const boqUpdateResponse = await fetch(apiUrl(`/api/contracts/${selectedContract.contract_id}/update-boq`), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(userId ? { 'X-User-ID': userId } : {})
              },
              body: JSON.stringify({
                boq_items: selectedBoqItemsList,
                change_numbers: allSelectedDCs  // Include all selected DCs
              })
            });

            if (!boqUpdateResponse.ok) {
              setBudgetLineLoading(false);
              const errorData = await boqUpdateResponse.json();
              throw new Error(errorData.error || 'Failed to update BOQ items');
            }

            console.log('✅ VO BOQ items updated successfully');

            // Refetch the updated contract with its lines to refresh Budget Lines section
            const refreshedContractResponse = await fetch(apiUrl(`/api/contracts/${selectedContract.contract_id}?include_lines=true`), {
              headers: { ...(userId ? { 'X-User-ID': userId } : {}) }
            });

            if (refreshedContractResponse.ok) {
              const refreshedContract = await refreshedContractResponse.json();
              setSelectedContract(refreshedContract);
              console.log('✅ Refreshed VO data with updated BOQ items');
            }

            setBudgetLineLoading(false); // Hide loading
          } catch (boqErr: any) {
            console.error('Failed to update VO BOQ items:', boqErr);
            setSnackbar({ open: true, message: `VO saved but BOQ update failed: ${boqErr.message}`, severity: 'warning' });
            setSaving(false);
            return;
          }
        }

        // Show appropriate message based on whether it's a VO or Contract
        const isVO = selectedContract?.contract_type === 'variation_order';
        const entityType = isVO ? 'VO' : 'Contract';
        const message = dialogMode === 'create' ? `${entityType} created.` : `${entityType} updated.`;
        setSnackbar({ open: true, message, severity: 'success' });
        // Clear focus from save button before closing dialog to prevent accessibility warning
        if (saveButtonRef.current && 'blur' in saveButtonRef.current) {
          (saveButtonRef.current as HTMLElement).blur();
        }
        // Small delay to ensure blur happens before dialog closes
        setTimeout(() => {
          handleCloseDialog();
          fetchContracts();
          window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
        }, 10);
      })
      .catch(err => {
        // Clear focus from save button on error to prevent accessibility issues
        if (saveButtonRef.current && 'blur' in saveButtonRef.current) {
          (saveButtonRef.current as HTMLElement).blur();
        }
        setDialogError(err.message);
        setSaving(false);
      });
  };

  // DataGrid handlers - define before useMemo
  const handleContractEditClick = useCallback((id) => {
    const contract = contracts.find(c => c.contract_id === id);
    if (contract) {
      handleOpenEdit(contract);
    }
  }, [contracts, handleOpenEdit]);

  const handleContractSaveClick = useCallback((id) => {
    // TODO: Implement inline save
    console.log('Save contract:', id);
  }, []);

  const handleContractCancelClick = useCallback((id) => {
    // TODO: Implement inline cancel
    console.log('Cancel contract edit:', id);
  }, []);

  const handleContractDeleteClick = useCallback((id) => {
    const contract = contracts.find(c => c.contract_id === id);
    setItemToDelete(contract);
    setDeleteType('contract');
    setDeleteDialogOpen(true);
  }, [contracts]);

  // Handle contract/VO row click to select it
  const handleContractRowClick = useCallback(async (contract: any) => {
    // Select the contract/VO to show its budget lines
    setSelectedContract(contract);
  }, []);

  // VO-specific handlers
  const handleVODeleteClick = useCallback((vo: any) => {
    setItemToDelete(vo);
    setDeleteType('vo');
    setDeleteDialogOpen(true);
  }, []);

  const handleVOEditClick = useCallback((vo: any) => {
    // Use the same edit dialog as contracts - VOs are stored in the same table
    console.log('Opening edit for VO:', vo);
    handleOpenEdit(vo);
  }, [handleOpenEdit]);

  // Budget Line Management Functions
  const handleAddBudgetLine = useCallback(() => {
    if (!selectedContract) return;

    setBudgetLineLoading(true); // Show loading in budget lines DataGrid
    setLoading(true); // Show loading in main contracts table

    const newLine = {
      id: `temp_${Date.now()}`, // Temporary ID for new lines
      contract_id: selectedContract.contract_id,
      line_no: (selectedContract.lines?.length || 0) + 1,
      description: '',
      qty: 1,
      uom: '',
      unit_price: 0,
      category_id: '' // Use empty string instead of null for singleSelect compatibility
    };

    const updatedLines = [...(selectedContract.lines || []), newLine];
    setSelectedContract(prev => ({ ...prev, lines: updatedLines }));
    setBudgetLineRowModesModel(prev => ({ ...prev, [newLine.id]: { mode: GridRowModes.Edit } }));

    // Hide loading indicators after state updates
    setTimeout(() => {
      setBudgetLineLoading(false);
      setLoading(false);
    }, 100);
  }, [selectedContract]);

  const handleBudgetLineEditClick = useCallback((id) => {
    setBudgetLineRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.Edit } }));
  }, []);

  const handleBudgetLineSaveClick = useCallback(async (id) => {
    try {
      console.log('💾 Save clicked for ID:', id);
      console.log('🔍 selectedContract:', selectedContract);
      console.log('🔍 selectedContract.lines:', selectedContract?.lines);
      
      // Early validation - but allow empty lines array
      if (!selectedContract) {
        console.error('No contract selected for saving');
        setSnackbar({ open: true, message: 'Error: No contract selected', severity: 'error' });
        return;
      }

      // Exit edit mode first - this should trigger DataGrid to commit any pending edits
      setBudgetLineRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.View } }));
      
      // Add a small delay to allow processRowUpdate to complete and update state first
      // This prevents race condition where manual save uses stale data
      setTimeout(() => {
        setPendingSaveId(id);
      }, 100); // Wait for processRowUpdate to finish
      
    } catch (error) {
      console.error('Error in save click handler:', error);
      setSnackbar({ open: true, message: 'Failed to save budget line', severity: 'error' });
    }
  }, [selectedContract]);

  const handleBudgetLineCancelClick = useCallback((id) => {
    setBudgetLineRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.View } }));
    
    // If it's a new line being cancelled, remove it
    if (String(id).startsWith('temp_')) {
      setSelectedContract(prev => ({
        ...prev,
        lines: prev.lines.filter(line => line.id !== id)
      }));
    }
  }, []);

  const handleBudgetLineDeleteClick = useCallback((id) => {
    // Early validation
    if (!selectedContract) {
      console.error('No contract selected for deletion');
      setSnackbar({ open: true, message: 'Error: No contract selected', severity: 'error' });
      return;
    }

    // Open delete dialog
    const lineToDelete = selectedContract.lines?.find(line => line.id === id);
    setItemToDelete({ ...lineToDelete, budgetLineId: id });
    setDeleteType('budgetLine');
    setDeleteDialogOpen(true);
  }, [selectedContract]);

  // Actual delete logic moved to confirm handler
  const confirmDeleteBudgetLine = useCallback(async (id) => {
    if (!selectedContract) return;

    // If it's a temp line, just remove from state
    if (String(id).startsWith('temp_')) {
      setSelectedContract(prev => {
        if (!prev || !prev.lines) return prev;
        return {
          ...prev,
          lines: prev.lines.filter(line => line.id !== id)
        };
      });
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      return;
    }

    // Check if selectedContract and lines exist
    if (!selectedContract || !selectedContract.lines) {
      console.error('No contract or lines available for deletion');
      setSnackbar({ open: true, message: 'Error: No contract selected or no lines available', severity: 'error' });
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      return;
    }

    try {
      setBudgetLineLoading(true); // Show loading state
      setLoading(true); // Show loading in main contracts table
      setDeleting(true);

        // Update the contract by removing the line and sending updated contract
        const remainingLines = selectedContract.lines.filter(line => line.id !== id);
        const linesToSave = remainingLines.map(line => {
          // Handle category_id properly - only send if it's a valid number
          let categoryId = line.category_id;
          if (categoryId === '' || categoryId === null || categoryId === undefined) {
            categoryId = null;
          }

          return {
            id: String(line.id).startsWith('temp_') ? undefined : line.id,
            line_no: line.line_no,
            description: line.description,
            qty: line.qty,
            uom: line.uom,
            unit_price: line.unit_price,
            category_id: categoryId,
            // Preserve source DC info if present
            source_change_number: line.source_change_number || undefined,
            source_dc_boq_line_id: line.source_dc_boq_line_id || undefined
          };
        });

        const payload: any = {
          contract_number: selectedContract.contract_number,
          title: selectedContract.title,
          original_value: selectedContract.original_value,
          currency: selectedContract.currency,
          signed_date: selectedContract.signed_date,
          rate_sheet_id: selectedContract.rate_sheet_id,
          lines: linesToSave
        };

        // Preserve VO-specific fields if this is a VO
        if (selectedContract.contract_type) {
          payload.contract_type = selectedContract.contract_type;
        }
        if (selectedContract.parent_contract_id) {
          payload.parent_contract_id = selectedContract.parent_contract_id;
        }

        const response = await fetch(apiUrl(`/api/contracts/${selectedContract.contract_id}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...(userId ? { 'X-User-ID': userId } : {}) },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          const updatedContract = await response.json();
          setSelectedContract(updatedContract);

          // Refetch all contracts to update total values immediately
          await fetchContracts();

          setBudgetLineLoading(false); // Hide loading state
          setLoading(false); // Hide loading in main contracts table
          setSnackbar({ open: true, message: 'Budget line deleted successfully', severity: 'success' });
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        } else {
          setBudgetLineLoading(false); // Hide loading state on error
          setLoading(false); // Hide loading in main contracts table on error
          const error = await response.json();
          console.error('❌ Failed to delete budget line. Status:', response.status, 'Error:', error);
          setSnackbar({ open: true, message: error.error || 'Failed to delete budget line', severity: 'error' });
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        }
      } catch (error) {
        setBudgetLineLoading(false); // Hide loading state on error
        setLoading(false); // Hide loading in main contracts table on error
        console.error('❌ Error deleting budget line:', error);
        setSnackbar({ open: true, message: 'Failed to delete budget line', severity: 'error' });
        setDeleteDialogOpen(false);
        setItemToDelete(null);
      } finally {
        setDeleting(false);
      }
  }, [selectedContract, userId, fetchContracts]);

  const handleBudgetLineRateSheetItemSelect = useCallback((lineId, rateSheetItem) => {
    if (rateSheetItem) {
      console.log('📋 Budget line selected rate sheet item:', rateSheetItem);
      console.log('🔍 Available categories:', categories);
      console.log('🔍 Looking for category_name:', rateSheetItem.category_name);
      
      // Find the category_id based on category_name with better matching
      const category = categories.find(cat => 
        cat.name === rateSheetItem.category_name || 
        cat.name?.toLowerCase() === rateSheetItem.category_name?.toLowerCase()
      );
      
      // Use empty string instead of null for singleSelect compatibility
      const categoryId = category ? category.category_id : '';
      console.log('🏷️ Auto-populating category:', rateSheetItem.category_name, '→', categoryId, category ? '✅' : '❌');
      
      setSelectedContract(prev => {
        const lineExists = prev.lines.some(line => line.id === lineId);
        console.log('🔍 Line exists in state:', lineExists, 'lineId:', lineId);
        
        if (!lineExists) {
          console.log('⚠️ Line not found in state, might be a new line in edit mode');
          return prev; // Don't update if line doesn't exist yet
        }
        
        return {
          ...prev,
          lines: prev.lines.map(line => 
            line.id === lineId 
              ? { 
                  ...line, 
                  description: rateSheetItem.item_description,
                  unit_price: rateSheetItem.unit_price || 0,
                  uom: rateSheetItem.unit_code || '',
                  category_id: categoryId,
                  rsi_id: rateSheetItem.rsi_id,
                  bli_id: rateSheetItem.bli_id
                } 
              : line
          )
        };
      });
    }
  }, [categories]);

  // Process row update for contracts (main DataGrid)
  const processContractRowUpdate = useCallback(async (newRow) => {
    console.log('🔄 processContractRowUpdate called with:', newRow);
    
    if (!canWrite) {
      throw new Error('You do not have permission to edit contracts.');
    }
    
    try {
      // Prepare the payload for the API call
      const payload = {
        project_id: projectId,
        contract_number: newRow.contract_number?.trim(),
        title: newRow.title?.trim() || null,
        original_value: newRow.original_value,
        currency: newRow.currency || 'SGD',
        signed_date: newRow.signed_date || null,
        rate_sheet_id: newRow.rate_sheet_id || null
      };
      
      console.log('📅 Date handling - newRow.signed_date:', newRow.signed_date, typeof newRow.signed_date);
      
      console.log('🚀 Updating contract via API:', payload);

      const response = await fetch(apiUrl(`/api/contracts/${newRow.contract_id}`), {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          ...(userId ? { 'X-User-ID': userId } : {})
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update contract');
      }
      
      const updatedContract = await response.json();
      console.log('✅ Contract updated successfully:', updatedContract);
      
      // Update local state
      setContracts(prev => prev.map(contract => 
        contract.contract_id === newRow.contract_id 
          ? { ...contract, ...updatedContract }
          : contract
      ));
      
      // Update selected contract if it's the one being edited
      if (selectedContract?.contract_id === newRow.contract_id) {
        setSelectedContract(prev => ({ ...prev, ...updatedContract }));
      }
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Contract updated successfully',
        severity: 'success'
      });
      
      return { ...newRow, ...updatedContract };
      
    } catch (error) {
      console.error('❌ Error updating contract:', error);
      setSnackbar({
        open: true,
        message: `Error updating contract: ${error.message}`,
        severity: 'error'
      });
      throw error; // This will revert the row to its original state
    }
  }, [canWrite, projectId, userId, selectedContract?.contract_id]);

  const budgetLineColumns: GridColDef[] = useMemo(() => [
    { field: 'line_no', headerName: 'Line #', width: 80, editable: canWrite, renderCell: (params) => {
      if (params.value !== null && params.value !== undefined && params.value !== '') return params.value;
      const rows = params.api.getSortedRowIds();
      return rows.indexOf(params.id) + 1;
    }},
    { 
      field: 'description', 
      headerName: 'Description', 
      width: 300, 
      editable: canWrite,
      renderEditCell: (params) => {
        return (
          <Autocomplete
            fullWidth
            size="small"
            options={rateSheetItems}
            getOptionLabel={(option) => option.item_description || ''}
            value={rateSheetItems.find(item => item.item_description === params.value) || null}
            noOptionsText={rateSheetItems.length === 0 ? "No rate sheet items available" : "No matching items found"}
            onChange={(event, newValue) => {
              console.log('🎯 Autocomplete onChange triggered:', { lineId: params.id, newValue });
              if (newValue) {
                // Find the category_id based on category_name
                const category = categories.find(cat => 
                  cat.name === newValue.category_name || 
                  cat.name?.toLowerCase() === newValue.category_name?.toLowerCase()
                );
                const categoryId = category ? category.category_id : '';
                
                console.log('🔄 Updating fields via DataGrid API:', {
                  description: newValue.item_description,
                  unit_price: newValue.unit_price || 0,
                  uom: newValue.unit_code || '',
                  category_id: categoryId
                });
                
                // Immediately update the description field
                params.api.setEditCellValue({ id: params.id, field: 'description', value: newValue.item_description || '' });
                
                // Update other fields with a slight delay to ensure proper sequencing
                setTimeout(() => {
                  try {
                    params.api.setEditCellValue({ id: params.id, field: 'unit_price', value: newValue.unit_price || 0 });
                    console.log('Updated unit_price to:', newValue.unit_price);
                  } catch (e) { console.error('Error setting unit_price:', e); }
                  
                  try {
                    params.api.setEditCellValue({ id: params.id, field: 'uom', value: newValue.unit_code || '' });
                    console.log('Updated uom to:', newValue.unit_code);
                  } catch (e) { console.error('Error setting uom:', e); }
                  
                  try {
                    params.api.setEditCellValue({ id: params.id, field: 'category_id', value: categoryId });
                    console.log('Updated category_id to:', categoryId);
                  } catch (e) { console.error('Error setting category_id:', e); }
                }, 100);
                
                // Also update the state for persistence
                handleBudgetLineRateSheetItemSelect(params.id, newValue);
              } else {
                // If cleared, just update description
                console.log('🔄 Clearing description field');
                params.api.setEditCellValue({ id: params.id, field: 'description', value: '' });
              }
            }}
            renderInput={(inputParams) => (
              <TextField
                {...inputParams}
                variant="standard"
                placeholder="Search and select item..."
                InputProps={{
                  ...inputParams.InputProps,
                  disableUnderline: true,
                  sx: { fontSize: '0.875rem' }
                }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <Box component="li" key={key} {...otherProps} sx={{ display: 'block', py: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.item_description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.category_name} • {option.unit_code} • ${option.unit_price}
                  </Typography>
                </Box>
              );
            }}
            filterOptions={(options, { inputValue }) => {
              return options.filter(option =>
                option.item_description?.toLowerCase().includes(inputValue.toLowerCase()) ||
                option.category_name?.toLowerCase().includes(inputValue.toLowerCase())
              );
            }}
          />
        );
      }
    },
    {
      field: 'qty',
      headerName: 'Qty',
      width: 100,
      type: 'number',
      editable: canWrite
    },
    { field: 'uom', headerName: 'UOM', width: 100, editable: canWrite },
    {
      field: 'unit_price',
      headerName: 'Unit Price',
      width: 130,
      type: 'number',
      editable: canWrite
    },
    { 
      field: 'category_id', 
      headerName: 'Category', 
      width: 200,
      type: 'singleSelect',
      valueOptions: [
        { value: '', label: '-- Select Category --' },
        ...categories.map(cat => ({ value: cat.category_id, label: cat.name }))
      ],
      editable: canWrite,
      renderCell: (params) => {
        if (!params.value) return '';
        const category = categories.find(cat => cat.category_id === params.value);
        return category ? category.name : '';
      }
    },
    {
      field: 'line_total',
      headerName: 'Line Total',
      width: 130,
      type: 'number',
      editable: false,
      valueGetter: (value, row) => {
        if (!row) return 0;
        // Always calculate from qty and unit_price for real-time updates
        const qty = row.qty || 0;
        const price = row.unit_price || 0;
        return qty * price;
      },
      valueFormatter: (value) => {
        if (value == null) return '';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'decimal',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(numValue);
      },
    },
    // {
    //   field: 'source_change_number',
    //   headerName: 'Source DC',
    //   width: 120,
    //   editable: false,
    //   renderCell: (params) => {
    //     if (!params.value) return '';
    //     return (
    //       <Badge
    //         badgeContent={params.value}
    //         color="info"
    //         sx={{
    //           '& .MuiBadge-badge': {
    //             position: 'static',
    //             transform: 'none',
    //             fontSize: '0.65rem',
    //             padding: '2px 6px'
    //           }
    //         }}
    //       />
    //     );
    //   },
    // },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = budgetLineRowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              onClick={() => handleBudgetLineSaveClick(id)}
              key="save"
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={() => handleBudgetLineCancelClick(id)}
              color="inherit"
              key="cancel"
            />,
          ];
        }

        return [
          ...(canWrite ? [
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              className="textPrimary"
              onClick={() => handleBudgetLineEditClick(id)}
              color="inherit"
              key="edit"
            />,
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => handleBudgetLineDeleteClick(id)}
              key="delete"
            />,
          ] : []),
        ];
      },
    },
  ], [canWrite, rateSheetItems, categories, budgetLineRowModesModel, handleBudgetLineRateSheetItemSelect, handleBudgetLineEditClick, handleBudgetLineSaveClick, handleBudgetLineCancelClick, handleBudgetLineDeleteClick]) as GridColDef[];

  // Flatten contracts and VOs for display (VOs shown as sub-rows when contract is expanded)
  const flattenedRows = useMemo(() => {
    // Return all contracts and VOs at the same level (no subrows)
    return contracts.map((contract) => ({
      ...contract,
      rowType: contract.contract_type === 'variation_order' ? 'vo' : 'contract'
    }));
  }, [contracts]);

  const contractColumns: any[] = useMemo(() => [
    {
      field: 'contract_number',
      headerName: 'Contract Number',
      width: 200,
      editable: !canWrite ? false : true,
      renderCell: (params) => {
        return (
          <Typography variant="body2">
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: 'contract_type',
      headerName: 'Type',
      width: 120,
      editable: false,
      renderCell: (params) => {
        const isVO = params.value === 'variation_order';
        return (
          <Chip
            label={isVO ? 'VO' : 'Contract'}
            size="small"
            color={isVO ? 'secondary' : 'primary'}
            variant="outlined"
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
    },
    {
      field: 'title',
      headerName: 'Title',
      width: 300,
      editable: !canWrite ? false : true,
    },
    {
      field: 'original_value',
      headerName: 'Original Contract Value',
      width: 170,
      editable: (params) => {
        // VOs cannot edit original value
        if (params.row && params.row.contract_type === 'variation_order') return false;
        return canWrite;
      },
      type: 'number',
      valueGetter: (value, row) => {
        return value || 0;
      },
      valueFormatter: (value) => {
        if (value == null || value === '') return '';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'decimal',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(numValue);
      },
    },
    // BOQ Value column - shows total value of all BOQ items (qty * unit_price)
    {
      field: 'boq_value',
      headerName: 'Original BOQ Value',
      width: 150,
      editable: false,
      type: 'number',
      valueGetter: (value, row) => {
        if (!row || !row.lines) return 0;
        // Calculate total of all budget line items (qty * unit_price)
        const total = row.lines.reduce((sum, line) => {
          if (!line) return sum;
          const qty = line.qty || 0;
          const price = line.unit_price || 0;
          return sum + (qty * price);
        }, 0);
        return total;
      },
      valueFormatter: (value) => {
        if (value == null || value === '') return '';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'decimal',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(numValue);
      },
    },
    {
      field: 'budget',
      headerName: 'Approved Budget',
      width: 150,
      editable: false,
      type: 'number',
      valueGetter: (value, row) => {
        return value != null ? value : null;
      },
      valueFormatter: (value) => {
        if (value == null) return 'N/A';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return 'N/A';
        return new Intl.NumberFormat('en-US', {
          style: 'decimal',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(numValue);
      },
    },
    {
      field: 'spent',
      headerName: 'Spent (MR/PO)',
      width: 150,
      editable: false,
      type: 'number',
      valueGetter: (value, row) => {
        return (row.total_value || 0) - (row.remaining_balance || 0);
      },
      valueFormatter: (value) => {
        if (value == null || value === '') return '';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'decimal',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(numValue);
      },
    },
    {
      field: 'remaining_balance',
      headerName: 'Budget Balance',
      width: 150,
      editable: false,
      type: 'number',
      valueGetter: (value, row) => {
        return value;
      },
      valueFormatter: (value) => {
        if (value == null || value === '') return '';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '';
        return new Intl.NumberFormat('en-US', {
          style: 'decimal',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(numValue);
      },
    },
    {
      field: 'currency',
      headerName: 'Currency',
      width: 100,
      editable: !canWrite ? false : true,
      type: 'singleSelect',
      valueOptions: ['SGD', 'USD', 'EUR', 'GBP', 'AUD', 'MYR'],
    },
    {
      field: 'contract_type',
      headerName: 'Type',
      width: 120,
      editable: false,
      renderCell: (params) => {
        const isVO = params.value === 'variation_order';
        return (
          <Badge
            badgeContent={isVO ? 'VO' : 'Contract'}
            color={isVO ? 'secondary' : 'primary'}
            sx={{
              '& .MuiBadge-badge': {
                position: 'static',
                transform: 'none',
                fontSize: '0.7rem',
                padding: '4px 8px'
              }
            }}
          />
        );
      },
    },
    {
      field: 'signed_date',
      headerName: 'Signed Date',
      width: 120,
      editable: !canWrite ? false : true,
      type: 'date',
      valueGetter: (value: any) => {
        if (!value) return null;
        try {
          // Handle both Date objects and date strings
          if (value instanceof Date) {
            return value;
          }
          // If it's a string, parse it as a local date to avoid timezone issues
          const dateStr = (value as any).toString();
          if (dateStr.includes('T') || dateStr.includes('Z')) {
            // ISO string - convert to local date
            const date = new Date(dateStr);
            return new Date(date.getFullYear(), date.getMonth(), date.getDate());
          } else {
            // Assume local date string (YYYY-MM-DD format)
            const [year, month, day] = dateStr.split('-').map(Number);
            return new Date(year, month - 1, day);
          }
        } catch (error) {
          console.error('Error parsing date:', value, error);
          return null;
        }
      },
      valueSetter: (value, row) => {
        if (!value) return { ...row, signed_date: null };
        try {
          // Ensure we store the date as YYYY-MM-DD string to avoid timezone issues
          let dateToSet;
          if (value instanceof Date) {
            dateToSet = value;
          } else {
            dateToSet = new Date(value);
          }
          
          if (isNaN(dateToSet.getTime())) {
            return { ...row, signed_date: null };
          }
          
          // Format as YYYY-MM-DD to avoid timezone conversion issues
          const year = dateToSet.getFullYear();
          const month = String(dateToSet.getMonth() + 1).padStart(2, '0');
          const day = String(dateToSet.getDate()).padStart(2, '0');
          const formattedDate = `${year}-${month}-${day}`;
          
          return { ...row, signed_date: formattedDate };
        } catch (error) {
          console.error('Error setting date:', value, error);
          return { ...row, signed_date: null };
        }
      },
      valueFormatter: (value: any) => {
        if (!value) return '';
        try {
          let date;
          if (value instanceof Date) {
            date = value;
          } else {
            // Parse as local date to avoid timezone issues
            const dateStr = (value as any).toString();
            if (dateStr.includes('-')) {
              const [year, month, day] = dateStr.split('-').map(Number);
              date = new Date(year, month - 1, day);
            } else {
              date = new Date(value);
            }
          }
          
          if (isNaN(date.getTime())) return '';
          
          // Format as YYYY/MM/DD
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}/${month}/${day}`;
        } catch (error) {
          console.error('Error formatting date:', value, error);
          return '';
        }
      },
    },
    {
      field: 'rate_sheet_name',
      headerName: 'Rate Sheet',
      width: 180,
      editable: false,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      cellClassName: 'actions',
      getActions: ({ id, row }) => {
        const isInEditMode = contractRowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              onClick={() => handleContractSaveClick(id)}
              key="save"
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={() => handleContractCancelClick(id)}
              color="inherit"
              key="cancel"
            />,
          ];
        }

        const isVO = row.rowType === 'vo';
        const count = row.document_count || 0;

        return [
          <GridActionsCellItem
            icon={
              <Badge
                badgeContent={count}
                color={count > 0 ? 'primary' : 'default'}
                max={99}
              >
                <AttachFileIcon />
              </Badge>
            }
            label="Documents"
            onClick={() => {
              setSelectedContractForDocs(row);
              setDocumentsDialogOpen(true);
            }}
            key="documents"
            showInMenu={false}
          />,
        ...(canWrite ? [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={() => isVO ? handleVOEditClick(row) : handleContractEditClick(id)}
            color="inherit"
            key="edit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => isVO ? handleVODeleteClick(row) : handleContractDeleteClick(id)}
            key="delete"
          />,
        ] : []),
        ];
      },
    },
  ], [contractRowModesModel, canWrite, contracts, handleContractEditClick, handleContractSaveClick, handleContractCancelClick, handleContractDeleteClick, handleContractRowClick, handleVOEditClick, handleVODeleteClick]);

  // Unified delete confirmation handler
  const handleConfirmDelete = useCallback(async () => {
    if (!itemToDelete) return;

    setDeleting(true);

    if (deleteType === 'contract') {
      try {
        const response = await fetch(apiUrl(`/api/contracts/${itemToDelete.contract_id}`), {
          method: 'DELETE',
          headers: { ...(userId ? { 'X-User-ID': userId } : {}) },
        });
        if (response.ok) {
          setContracts(prev => prev.filter(contract => contract.contract_id !== itemToDelete.contract_id));
          setSelectedContract(prev => prev?.contract_id === itemToDelete.contract_id ? null : prev);
          setSnackbar({ open: true, message: 'Contract deleted successfully', severity: 'success' });
          fetchContracts();
          window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
        } else {
          const error = await response.json();
          setSnackbar({ open: true, message: error.error || 'Failed to delete contract', severity: 'error' });
        }
      } catch (error) {
        setSnackbar({ open: true, message: 'Failed to delete contract', severity: 'error' });
      }
    } else if (deleteType === 'vo') {
      try {
        const response = await fetch(apiUrl(`/api/contracts/${itemToDelete.contract_id}`), {
          method: 'DELETE',
          headers: { ...(userId ? { 'X-User-ID': userId } : {}) },
        });
        if (response.ok) {
          setSelectedContract(prev => prev?.contract_id === itemToDelete.contract_id ? null : prev);
          setSnackbar({ open: true, message: 'VO deleted successfully', severity: 'success' });
          fetchContracts(); // Refresh all contracts/VOs
          window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
        } else {
          const error = await response.json();
          setSnackbar({ open: true, message: error.error || 'Failed to delete VO', severity: 'error' });
        }
      } catch (error) {
        setSnackbar({ open: true, message: 'Failed to delete VO', severity: 'error' });
      }
    } else if (deleteType === 'budgetLine') {
      await confirmDeleteBudgetLine(itemToDelete.budgetLineId);
    }

    setDeleting(false);
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  }, [itemToDelete, deleteType, userId, fetchContracts, confirmDeleteBudgetLine]);

  // Delete All handlers
  const handleDeleteAllClick = () => {
    if (!canDeleteAll) return;
    setDeleteAllDialogOpen(true);
  };

  const handleDeleteAllConfirm = async () => {
    if (!projectId) return;

    setDeletingAll(true);
    try {
      const response = await fetch(apiUrl(`/api/contracts/delete-all?project_id=${projectId}`), {
        method: 'DELETE',
        headers: { ...(userId ? { 'X-User-ID': userId } : {}) },
      });

      if (response.ok) {
        fetchContracts();
        window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
        setSelectedContract(null);
        setSnackbar({ open: true, message: 'All contracts deleted successfully', severity: 'success' });
        setDeleteAllDialogOpen(false);
      } else {
        const error = await response.json();
        setSnackbar({ open: true, message: error.error || 'Failed to delete all contracts', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete all contracts', severity: 'error' });
    } finally {
      setDeletingAll(false);
    }
  };

  const handleDeleteAllCancel = () => {
    setDeleteAllDialogOpen(false);
  };

  if (access === 'none') {
    return (
      <Paper sx={{ p: 4 }}>
        <Typography>You do not have access to view contracts.</Typography>
      </Paper>
    );
  }

  if (!projectId) {
    return (
      <Paper sx={{ p: 4 }}>
        <Typography>Select a project to manage contracts.</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', margin: '0 24px 16px 24px' }}>
        {/* Add Contract / VO Button */}
        {canWrite && (
          <>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddMenu}>
              Add Contract / VO
            </Button>
            <Menu
              anchorEl={addMenuAnchorEl}
              open={addMenuOpen}
              onClose={handleCloseAddMenu}
            >
              <MenuItem onClick={handleOpenCreate}>Create Contract</MenuItem>
              <MenuItem onClick={handleOpenCreateVOFromMenu}>Create VO</MenuItem>
            </Menu>
          </>
        )}
      </Box>
      {/* Contracts Section */}
      {pageError && (
        <Alert severity="error" sx={{ margin: '0 24px 16px 24px' }}>{pageError}</Alert>
      )}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden', background: 'white', margin: '0 24px' }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 8 }}>
            <CircularProgress size={32} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">Loading contracts...</Typography>
          </Box>
        ) : contracts.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8 }}>
            <Typography variant="body1" color="text.secondary">No contracts recorded yet.</Typography>
          </Box>
        ) : (
          <DataGrid
                rows={flattenedRows}
                columns={contractColumns}
                getRowId={(row) => row.contract_id}
                loading={loading}
                onRowClick={(params) => {
                  console.log('🔍 Row selected:', params.row);
                  // Don't handle clicks on loading rows
                  if (params.row && params.row.rowType === 'loading') {
                    return;
                  }
                  if (params.row) {
                    // Handle both contracts and VOs
                    handleContractRowClick(params.row);

                    // Fetch rate sheet items if available
                    if (params.row.rate_sheet_id) {
                      fetchRateSheetItems(params.row.rate_sheet_id);
                    }
                  } else {
                    setSelectedContract(null);
                    setRateSheetItems([]);
                  }
                }}
                isCellEditable={(params) => params.row.rowType !== 'loading'}
                processRowUpdate={processContractRowUpdate}
                onProcessRowUpdateError={(error) => {
                  console.error('❌ DataGrid processRowUpdate error:', error);
                  setSnackbar({
                    open: true,
                    message: `Error updating contract: ${error.message}`,
                    severity: 'error'
                  });
                }}
                autoHeight
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 25 }
                  }
                }}
                pageSizeOptions={[25, 50, 100]}
                rowSelectionModel={selectedContract ? { type: 'include', ids: new Set([selectedContract.contract_id]) } : { type: 'include', ids: new Set() }}
                sx={dataGridSx}
              />
        )}
      </Box>

      {/* Import/Export Buttons */}
      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', margin: '16px 24px 0 24px' }}>
        <Button variant="outlined" onClick={handleDownloadTemplate}>
          Export Template
        </Button>
        <Button
          variant="contained"
          onClick={handleExportExcel}
          disabled={excelExporting || contracts.length === 0}
        >
          {excelExporting ? 'Exporting...' : 'Export To Excel'}
        </Button>
        <Button
          variant="contained"
          onClick={() => fileInputRef.current?.click()}
          disabled={excelImporting || !canWrite}
        >
          {excelImporting ? 'Importing...' : 'Import From Excel'}
        </Button>
        {canDeleteAll && (
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAllClick}
            disabled={deletingAll || loading}
          >
            {deletingAll ? 'Deleting...' : 'Delete All'}
          </Button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleImportExcel}
          style={{ display: 'none' }}
        />
      </Box>

      {/* Budget Lines Section */}
      <Box sx={{ mt: 3, margin: '24px 24px 0 24px' }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Contract Budget Lines
        </Typography>

          {selectedContract ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {selectedContract.contract_number} - {selectedContract.title}
                  </Typography>
                </Box>
                {canWrite && (
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddBudgetLine}
                      disabled={!selectedContract || !canWrite}
                    >
                      Add Budget Line
                    </Button>
                  </Stack>
                )}
              </Box>

              <Box sx={{ width: '100%' }}>
                <DataGrid
                    rows={selectedContract.lines?.filter(line => line && typeof line === 'object' && line.id) || []}
                    columns={budgetLineColumns}
                    getRowId={(row) => row.id}
                    editMode="row"
                    rowModesModel={budgetLineRowModesModel}
                    onRowModesModelChange={setBudgetLineRowModesModel}
                    onRowDoubleClick={(params) => {
                      if (canWrite && params.row && !String(params.id).startsWith('temp_')) {
                        setBudgetLineRowModesModel(prev => ({ ...prev, [params.id]: { mode: GridRowModes.Edit } }));
                      }
                    }}
                    onRowEditStop={(params, event) => {
                      if (params.reason === GridRowEditStopReasons.rowFocusOut) {
                        event.defaultMuiPrevented = true;
                      }
                    }}
                    processRowUpdate={processBudgetLineRowUpdate}
                    onProcessRowUpdateError={(error) => {
                      console.error('Budget line DataGrid processRowUpdate error:', error);
                    }}
                    loading={budgetLineLoading}
                    autoHeight
                    disableRowSelectionOnClick
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 10 } }
                    }}
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                      toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 300 },
                        printOptions: { disableToolbarButton: true },
                        csvOptions: { disableToolbarButton: true },
                      },
                    }}
                    rowHeight={48}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      '& .MuiDataGrid-toolbarContainer': {
                        padding: '8px 12px',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        gap: 1,
                      },
                      '& .MuiDataGrid-cell': {
                        paddingX: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        fontSize: '0.875rem'
                      },
                      '& .MuiDataGrid-row': {
                        '&:nth-of-type(even)': {
                          backgroundColor: 'action.hover',
                          opacity: 0.8
                        },
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          cursor: canWrite ? 'pointer' : 'default'
                        }
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: 'grey.100',
                        borderBottom: '2px solid',
                        borderColor: 'divider',
                        fontSize: '0.8rem',
                        fontWeight: 700
                      },
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 700,
                        fontSize: '0.8rem'
                      },
                      '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                        outline: 'none'
                      },
                      '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                        outline: 'none'
                      }
                    }}
                  />
              </Box>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Select a contract to see its budget lines.
            </Typography>
          )}
      </Box>

      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        disableRestoreFocus={true}
        TransitionProps={{
          onExited: () => {
            // Ensure focus is cleared after dialog animation completes
            if (document.activeElement && document.activeElement !== document.body && 'blur' in document.activeElement) {
              (document.activeElement as HTMLElement).blur();
            }
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {dialogMode === 'create' ? 'Add Contract' : 'Edit Contract'}
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>
          )}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                required
                fullWidth
                label="Contract Number"
                name="contract_number"
                value={formValues.contract_number}
                onChange={handleFormChange}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth
                label="Title"
                name="title"
                value={formValues.title}
                onChange={handleFormChange}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Signed Date"
                name="signed_date"
                type="date"
                value={formValues.signed_date || ''}
                onChange={handleFormChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {/* Hide Original Value for VOs - they only have BOQ value */}
            {formValues.contract_type !== 'variation_order' && (
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  required
                  fullWidth
                  label="Original Value"
                  name="original_value"
                  type="number"
                  value={formValues.original_value}
                  onChange={handleFormChange}
                  inputProps={{ step: '0.01' }}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                fullWidth
                label="Currency"
                name="currency"
                value={formValues.currency}
                onChange={handleFormChange}
              >
                {['SGD', 'USD', 'EUR', 'GBP', 'AUD', 'MYR'].map(code => (
                  <MenuItem key={code} value={code}>{code}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                fullWidth
                label="Rate Sheet"
                name="rate_sheet_id"
                value={formValues.rate_sheet_id || ''}
                onChange={handleFormChange}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {rateSheets.map(sheet => (
                  <MenuItem key={sheet.rate_sheet_id} value={sheet.rate_sheet_id}>
                    {sheet.sheet_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* DC/BOQ Selection for VOs in Edit Mode */}
            {dialogMode === 'edit' && formValues.contract_type === 'variation_order' && (
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Manage Design Changes in this VO
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select or deselect BOQ items from design changes. Changes will be saved when you click Save.
                </Typography>
                {designChanges.length === 0 ? (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    No design changes available for this project.
                  </Alert>
                ) : (
                  <Box sx={{ maxHeight: 400, overflowY: 'auto', mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                    {designChanges.map((dc: any) => {
                      const isExpanded = voExpandedDCs.includes(dc.change_number);
                      const boqItems = voDcBoqData[dc.change_number] || [];
                      const selectedItems = voSelectedBoqItems[dc.change_number] || [];
                      const allSelected = boqItems.length > 0 && selectedItems.length === boqItems.length;
                      const someSelected = selectedItems.length > 0 && selectedItems.length < boqItems.length;

                      return (
                        <Box
                          key={dc.change_number}
                          sx={{
                            mb: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            overflow: 'hidden'
                          }}
                        >
                          {/* DC Header */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              p: 1,
                              bgcolor: isExpanded ? 'action.hover' : 'transparent',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleVOToggleDCExpand(dc.change_number)}
                              sx={{ mr: 1 }}
                            >
                              {isExpanded ? <ExpandMoreIcon /> : <ExpandMoreIcon sx={{ transform: 'rotate(-90deg)' }} />}
                            </IconButton>
                            <Checkbox
                              checked={allSelected}
                              indeterminate={someSelected}
                              onChange={() => handleVODCSelect(dc.change_number)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Box sx={{ flex: 1 }} onClick={() => handleVOToggleDCExpand(dc.change_number)}>
                              <Typography variant="body2" fontWeight={600}>
                                {dc.change_number}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {dc.change_name || 'No name'} • {dc.boq_value ? `$${dc.boq_value.toFixed(2)}` : '$0.00'}
                              </Typography>
                            </Box>
                            {selectedItems.length > 0 && (
                              <Chip
                                label={`${selectedItems.length} selected`}
                                size="small"
                                color="primary"
                                sx={{ mr: 1 }}
                              />
                            )}
                          </Box>

                          {/* BOQ Items (Expanded) */}
                          {isExpanded && (
                            <Box sx={{ bgcolor: 'background.default', p: 1, pl: 6 }}>
                              {voLoadingBoqData ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
                                  <CircularProgress size={20} sx={{ mr: 2 }} />
                                  <Typography variant="caption" color="text.secondary">
                                    Loading BOQ items...
                                  </Typography>
                                </Box>
                              ) : boqItems.length === 0 ? (
                                <Typography variant="caption" color="text.secondary">
                                  No BOQ items in this Design Change
                                </Typography>
                              ) : (
                                boqItems.map((boqItem: any) => {
                                  const isItemSelected = selectedItems.includes(boqItem.dc_boq_line_id);
                                  return (
                                    <Box
                                      key={boqItem.dc_boq_line_id}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        py: 0.5,
                                        px: 1,
                                        borderRadius: 1,
                                        '&:hover': { bgcolor: 'action.hover' }
                                      }}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={isItemSelected}
                                        onChange={() => handleVOBoqItemSelect(dc.change_number, boqItem.dc_boq_line_id)}
                                      />
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" fontWeight={500}>
                                          {boqItem.item_description || 'No description'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          {boqItem.qty} {boqItem.unit_code} × ${boqItem.unit_price} = ${boqItem.line_total?.toFixed(2) || '0.00'}
                                          {boqItem.category_name && ` • ${boqItem.category_name}`}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  );
                                })
                              )}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
                {Object.values(voSelectedBoqItems).some(items => items.length > 0) && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    {Object.values(voSelectedBoqItems).reduce((sum, items) => sum + items.length, 0)} BOQ item(s) selected from{' '}
                    {Object.keys(voSelectedBoqItems).filter(dc => voSelectedBoqItems[dc].length > 0).length} DC(s)
                  </Alert>
                )}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
          <Button
            ref={saveButtonRef}
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Saving?' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={(snackbar.severity || 'info') as AlertColor} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Documents Dialog */}
      <Dialog
        open={documentsDialogOpen}
        onClose={() => setDocumentsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Contract Documents
            </Typography>
            {selectedContractForDocs && (
              <Typography variant="body2" color="text.secondary">
                {selectedContractForDocs.contract_number} - {selectedContractForDocs.title}
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setDocumentsDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedContractForDocs && (
            <ContractDocuments
              contractId={selectedContractForDocs.contract_id}
              projectId={projectId}
              canWrite={canWrite}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Import Design Changes Dialog */}
      <Dialog
        open={importDCDialogOpen}
        onClose={handleCloseImportDC}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Import Design Changes
          <IconButton onClick={handleCloseImportDC} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {importDCError && (
            <Alert severity="error" sx={{ mb: 2 }}>{importDCError}</Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select design changes to import into this contract. Items with the same budget line item ID will have their quantities merged.
          </Typography>
          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {designChanges.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No design changes available for this project.
              </Typography>
            ) : (
              designChanges.map((dc: any) => (
                <Box
                  key={dc.change_number}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    cursor: 'pointer',
                    bgcolor: selectedDCs.includes(dc.change_number) ? 'action.selected' : 'transparent',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                  onClick={() => handleDCSelect(dc.change_number)}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Checkbox
                      checked={selectedDCs.includes(dc.change_number)}
                      onChange={() => handleDCSelect(dc.change_number)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight={600}>
                        {dc.change_number}
                      </Typography>
                      {dc.change_name && (
                        <Typography variant="body2" color="text.secondary">
                          {dc.change_name}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>
              ))
            )}
          </Box>
          {selectedDCs.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {selectedDCs.length} design change{selectedDCs.length > 1 ? 's' : ''} selected
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDC} disabled={importingDC}>
            Cancel
          </Button>
          <Button
            onClick={handleImportDCSubmit}
            variant="contained"
            disabled={importingDC || selectedDCs.length === 0}
            startIcon={importingDC ? <CircularProgress size={16} /> : null}
          >
            {importingDC ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Variation Order Dialog */}
      <Dialog
        open={voDialogOpen}
        onClose={handleCloseVODialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Create Variation Order
          <IconButton onClick={handleCloseVODialog} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {voDialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>{voDialogError}</Alert>
          )}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                required
                fullWidth
                label="VO Number"
                name="contract_number"
                value={voFormValues.contract_number}
                onChange={handleVOFormChange}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth
                label="Title"
                name="title"
                value={voFormValues.title}
                onChange={handleVOFormChange}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Signed Date"
                name="signed_date"
                type="date"
                value={voFormValues.signed_date || ''}
                onChange={handleVOFormChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {/* VOs don't have original value - only BOQ value */}
            {/* <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                required
                fullWidth
                label="Original Value"
                name="original_value"
                type="number"
                value={voFormValues.original_value}
                onChange={handleVOFormChange}
                inputProps={{ step: '0.01' }}
              />
            </Grid> */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                fullWidth
                label="Currency"
                name="currency"
                value={voFormValues.currency}
                onChange={handleVOFormChange}
              >
                {['SGD', 'USD', 'EUR', 'GBP', 'AUD', 'MYR'].map(code => (
                  <MenuItem key={code} value={code}>{code}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                fullWidth
                label="Rate Sheet"
                name="rate_sheet_id"
                value={voFormValues.rate_sheet_id || ''}
                onChange={handleVOFormChange}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {rateSheets.map(sheet => (
                  <MenuItem key={sheet.rate_sheet_id} value={sheet.rate_sheet_id}>
                    {sheet.sheet_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* DC Selection Section */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Select Design Changes to Include in VO
              </Typography>
              {designChanges.length === 0 ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No design changes available for this project.
                </Alert>
              ) : (
                <Box sx={{ maxHeight: 400, overflowY: 'auto', mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                  {designChanges.map((dc: any) => {
                    const isExpanded = voExpandedDCs.includes(dc.change_number);
                    const boqItems = voDcBoqData[dc.change_number] || [];
                    const selectedItems = voSelectedBoqItems[dc.change_number] || [];
                    const allSelected = boqItems.length > 0 && selectedItems.length === boqItems.length;
                    const someSelected = selectedItems.length > 0 && selectedItems.length < boqItems.length;

                    return (
                      <Box
                        key={dc.change_number}
                        sx={{
                          mb: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          overflow: 'hidden'
                        }}
                      >
                        {/* DC Header */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 1,
                            bgcolor: isExpanded ? 'action.hover' : 'transparent',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleVOToggleDCExpand(dc.change_number)}
                            sx={{ mr: 1 }}
                          >
                            {isExpanded ? <ExpandMoreIcon /> : <ExpandMoreIcon sx={{ transform: 'rotate(-90deg)' }} />}
                          </IconButton>
                          <Checkbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={() => handleVODCSelect(dc.change_number)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Box sx={{ flex: 1 }} onClick={() => handleVOToggleDCExpand(dc.change_number)}>
                            <Typography variant="body2" fontWeight={600}>
                              {dc.change_number}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {dc.change_name || 'No name'} • {dc.boq_value ? `$${dc.boq_value.toFixed(2)}` : '$0.00'}
                            </Typography>
                          </Box>
                          {selectedItems.length > 0 && (
                            <Chip
                              label={`${selectedItems.length} selected`}
                              size="small"
                              color="primary"
                              sx={{ mr: 1 }}
                            />
                          )}
                        </Box>

                        {/* BOQ Items (Expanded) */}
                        {isExpanded && (
                          <Box sx={{ bgcolor: 'background.default', p: 1, pl: 6 }}>
                            {voLoadingBoqData ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
                                <CircularProgress size={20} sx={{ mr: 2 }} />
                                <Typography variant="caption" color="text.secondary">
                                  Loading BOQ items...
                                </Typography>
                              </Box>
                            ) : boqItems.length === 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                No BOQ items in this Design Change
                              </Typography>
                            ) : (
                              boqItems.map((boqItem: any) => {
                                const isItemSelected = selectedItems.includes(boqItem.dc_boq_line_id);
                                return (
                                  <Box
                                    key={boqItem.dc_boq_line_id}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      py: 0.5,
                                      px: 1,
                                      borderRadius: 1,
                                      '&:hover': { bgcolor: 'action.hover' }
                                    }}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={isItemSelected}
                                      onChange={() => handleVOBoqItemSelect(dc.change_number, boqItem.dc_boq_line_id)}
                                    />
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="caption" fontWeight={500}>
                                        {boqItem.item_description || 'No description'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        {boqItem.qty} {boqItem.unit_code} × ${boqItem.unit_price} = ${boqItem.line_total?.toFixed(2) || '0.00'}
                                        {boqItem.category_name && ` • ${boqItem.category_name}`}
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              })
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
              {Object.values(voSelectedBoqItems).some(items => items.length > 0) && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {Object.values(voSelectedBoqItems).reduce((sum, items) => sum + items.length, 0)} BOQ item(s) selected from{' '}
                  {Object.keys(voSelectedBoqItems).filter(dc => voSelectedBoqItems[dc].length > 0).length} DC(s)
                </Alert>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVODialog} disabled={creatingVO}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateVOSubmit}
            variant="contained"
            disabled={creatingVO}
            startIcon={creatingVO ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {creatingVO ? 'Creating...' : 'Create Variation Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {deleteType === 'contract' && `Are you sure you want to delete contract ${itemToDelete?.contract_number} and all its budget lines?`}
            {deleteType === 'vo' && `Are you sure you want to delete VO ${itemToDelete?.contract_number} and all its budget lines?`}
            {deleteType === 'budgetLine' && 'Are you sure you want to delete this budget line?'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Results Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={handleImportDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Import Results
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {importResults && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Created:</strong> {importResults.created || 0}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Errors:</strong> {importResults.total_errors || 0}
                </Typography>
              </Box>

              {importResults.errors && importResults.errors.length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Import Errors:
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {importResults.errors.map((err: any, idx: number) => (
                      <Typography key={idx} variant="body2" sx={{ mt: 0.5 }}>
                        Row {err.row}: {err.field} - {err.error}
                      </Typography>
                    ))}
                  </Box>
                </Alert>
              )}

              {(!importResults.errors || importResults.errors.length === 0) && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  All contracts/VOs imported successfully!
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleImportDialogClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog
        open={deleteAllDialogOpen}
        onClose={handleDeleteAllCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          <Typography variant="h6" fontWeight={600} color="error">
            Delete All Contracts
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body1">
            Are you sure you want to delete <strong>ALL</strong> contracts and VOs for this project?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will permanently remove all contracts, VOs, their budget lines, and contract-design-change links from the database.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleDeleteAllCancel} variant="outlined" disabled={deletingAll}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAllConfirm}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={deletingAll}
          >
            {deletingAll ? 'Deleting...' : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Contracts;
